/**
 * User Manager Service (ORCH-336, ORCH-21 §12)
 *
 * Purpose: The User Manager's calls to /core-api/users: the scoped member list,
 *          a person's profile and membership, role assignment, deactivate /
 *          reactivate / remove, bulk status changes, invitations and history.
 * Dependencies: apiService (auth headers and the active tenant)
 */

import apiService from './api';
import {
  AllMemberships,
  BulkStatusResult,
  CompanyOption,
  CurrentUserProfile,
  InvitationsPayload,
  InviteResult,
  Member,
  MemberFilter,
  MembershipHistoryEntry,
  UserDetail,
  UserErrorDetail,
  UserInviteAcceptPayload,
  UserListQuery,
  UserListResponseData,
  UserProfile,
  UserSelfUpdatePayload,
} from '../types/userManagement';

interface ApiEnvelope<T> {
  status: string;
  data: T;
  message?: string;
}

/** Drop empty values so they aren't sent as blank query parameters. */
const compact = (query: object): Record<string, string | number> =>
  Object.fromEntries(
    Object.entries(query).filter(
      ([, value]) => value !== undefined && value !== null && value !== ''
    )
  ) as Record<string, string | number>;

/**
 * One id per Save on the user card (ORCH-422): sent as `X-Change-Id` with every request
 * the Save makes, so the backend groups their audit rows into one History entry.
 */
export const newChangeId = (): string => crypto.randomUUID();

const changeHeaders = (changeId?: string) =>
  changeId ? { headers: { 'X-Change-Id': changeId } } : undefined;

const membership = (userId: string, tenantId: string) =>
  `/users/${userId}/memberships/${encodeURIComponent(tenantId)}`;

/** `tenant_id` is only sent when a system admin manages a tenant other than the active one. */
const bulkBody = (target: string[] | MemberFilter, tenantId?: string) => ({
  ...(Array.isArray(target) ? { user_ids: target } : { filter: compact(target) }),
  ...(tenantId ? { tenant_id: tenantId } : {}),
});

export const userManagementService = {
  async listUsers(query: UserListQuery = {}): Promise<UserListResponseData> {
    const response = await apiService.api.get<ApiEnvelope<UserListResponseData>>('/users', {
      params: compact(query),
    });
    return response.data.data;
  },

  /** The person's profile and their membership in the tenant; 404 outside the caller's scope. */
  async getUser(userId: string, tenantId?: string): Promise<UserDetail> {
    const response = await apiService.api.get<ApiEnvelope<UserDetail>>(`/users/${userId}`, {
      params: compact({ tenant_id: tenantId }),
    });
    return response.data.data;
  },

  /**
   * Change the shared profile: the person themselves, or a system admin in GLOBAL.
   * `changeId` is the Save's id (see `newChangeId`), so its changes are one History entry.
   */
  async updateProfile(
    userId: string,
    payload: UserSelfUpdatePayload,
    changeId?: string
  ): Promise<UserProfile> {
    const response = await apiService.api.patch<ApiEnvelope<UserProfile>>(
      `/users/${userId}`,
      payload,
      changeHeaders(changeId)
    );
    return response.data.data;
  },

  /**
   * The Companies a membership in the tenant may name, each with its heading: the
   * tenant's enabled and tenant-local agencies and advertisers, or in GLOBAL the
   * tenant's own organization (ORCH-398, ORCH-410 §4.2).
   */
  async companyOptions(tenantId: string): Promise<CompanyOption[]> {
    const response = await apiService.api.get<ApiEnvelope<{ options: CompanyOption[] }>>(
      '/users/company-options',
      { params: { tenant_id: tenantId } }
    );
    return response.data.data.options;
  },

  /**
   * Change the membership's Company and security attributes; with `roleIds`, add the
   * person to a tenant. `changeId` is the Save's id, shared with its other requests.
   */
  async updateMembership(
    userId: string,
    tenantId: string,
    metadata: Record<string, unknown>,
    options: { roleIds?: string[]; changeId?: string } = {}
  ): Promise<Member> {
    const response = await apiService.api.put<ApiEnvelope<Member>>(
      membership(userId, tenantId),
      { metadata, ...(options.roleIds ? { role_ids: options.roleIds } : {}) },
      changeHeaders(options.changeId)
    );
    return response.data.data;
  },

  /** Replace the roles the member holds in the tenant. `changeId` as for `updateProfile`. */
  async setRoles(
    userId: string,
    tenantId: string,
    roleIds: string[],
    changeId?: string
  ): Promise<Member> {
    const response = await apiService.api.post<ApiEnvelope<Member>>(
      `${membership(userId, tenantId)}/roles`,
      { role_ids: roleIds },
      changeHeaders(changeId)
    );
    return response.data.data;
  },

  /** Deactivate or reactivate the person in this tenant only. */
  async setActive(userId: string, tenantId: string, active: boolean): Promise<Member> {
    const response = await apiService.api.post<ApiEnvelope<Member>>(
      `${membership(userId, tenantId)}/${active ? 'reactivate' : 'deactivate'}`
    );
    return response.data.data;
  },

  /** Remove the person from this tenant. */
  async removeMember(userId: string, tenantId: string): Promise<void> {
    await apiService.api.delete(membership(userId, tenantId));
  },

  /** Deactivate or reactivate the selected people, or everyone matching the filters. */
  async bulkSetActive(
    target: string[] | MemberFilter,
    active: boolean,
    tenantId?: string
  ): Promise<BulkStatusResult> {
    const response = await apiService.api.post<ApiEnvelope<BulkStatusResult>>(
      `/users/bulk/${active ? 'activate' : 'deactivate'}`,
      bulkBody(target, tenantId)
    );
    return response.data.data;
  },

  async invite(payload: InvitationsPayload): Promise<InviteResult> {
    const response = await apiService.api.post<ApiEnvelope<InviteResult>>(
      '/users/invitations',
      payload
    );
    return response.data.data;
  },

  async acceptInvitation(
    payload: UserInviteAcceptPayload
  ): Promise<{ user_id: string; email: string }> {
    const response = await apiService.api.post<ApiEnvelope<{ user_id: string; email: string }>>(
      '/users/invitations/accept',
      payload
    );
    return response.data.data;
  },

  /**
   * Email the person a single-use link to choose a new password (ORCH-296). The admin
   * never sees or sets the password; their current one keeps working until the link is used.
   */
  async sendPasswordReset(
    userId: string,
    tenantId?: string
  ): Promise<{ sent_to: string; expires_at: string }> {
    const response = await apiService.api.post<
      ApiEnvelope<{ sent_to: string; expires_at: string }>
    >(`/users/${userId}/password-reset`, undefined, { params: compact({ tenant_id: tenantId }) });
    return response.data.data;
  },

  /** Public: set a new password with the token from a reset email (ORCH-296). */
  async confirmPasswordReset(payload: {
    token: string;
    password: string;
  }): Promise<{ email: string }> {
    const response = await apiService.api.post<ApiEnvelope<{ email: string }>>(
      '/auth/password-reset/confirm',
      payload
    );
    return response.data.data;
  },

  /** Every change on the user card, one entry per save, newest first (ORCH-422). */
  async getHistory(userId: string, tenantId?: string): Promise<MembershipHistoryEntry[]> {
    const response = await apiService.api.get<ApiEnvelope<MembershipHistoryEntry[]>>(
      `/users/${userId}/history`,
      { params: compact({ tenant_id: tenantId }) }
    );
    return response.data.data;
  },

  /** A system admin in GLOBAL gets the account status and every tenant. */
  async getMemberships(userId: string): Promise<AllMemberships> {
    const response = await apiService.api.get<ApiEnvelope<AllMemberships>>(
      `/users/${userId}/memberships`
    );
    return response.data.data;
  },

  /** Deactivate or reactivate the account itself (system admins in GLOBAL). */
  async setAccountActive(userId: string, active: boolean): Promise<AllMemberships> {
    const response = await apiService.api.patch<ApiEnvelope<AllMemberships>>(
      `/users/${userId}/account`,
      { active }
    );
    return response.data.data;
  },

  async getCurrentUserProfile(): Promise<CurrentUserProfile> {
    const response = await apiService.api.get<ApiEnvelope<CurrentUserProfile>>('/users/me');
    return response.data.data;
  },

  /** Change the caller's own profile. `changeId` as for `updateProfile`. */
  async updateCurrentUserProfile(
    payload: UserSelfUpdatePayload,
    changeId?: string
  ): Promise<UserProfile> {
    const response = await apiService.api.patch<ApiEnvelope<UserProfile>>(
      '/users/me',
      payload,
      changeHeaders(changeId)
    );
    return response.data.data;
  },

  /** The caller's own Company, roles and attributes in each tenant. */
  async getOwnMemberships(): Promise<Member[]> {
    const response = await apiService.api.get<ApiEnvelope<Member[]>>('/users/me/memberships');
    return response.data.data;
  },
};

/** The refusal a /users route sent: its code, message and details. */
export const userError = (error: unknown): UserErrorDetail => {
  const detail = (error as { response?: { data?: { detail?: UserErrorDetail | string } } })
    ?.response?.data?.detail;
  if (typeof detail === 'string') return { message: detail };
  return detail ?? {};
};

/** The server's message for a refused request, or a fallback. */
export const userErrorMessage = (error: unknown, fallback: string): string =>
  userError(error).message || fallback;

export default userManagementService;
